import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ProjectElementImport } from '@shared/models/element-import.model';
import { getImportLabelParts } from './import-display.helpers';

const useStyles = makeStyles()(() => ({
  root: {
    alignItems: 'center',
    color: 'rgba(0,0,0,0.5)',
    display: 'flex',
    flex: '1 1 0',
    minWidth: 0,
    overflow: 'hidden',
    WebkitTextFillColor: 'rgba(0,0,0,0.5)',
  },
  fileName: {
    display: 'block',
    flex: '1 1 0',
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: '1.3px',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  dateTime: {
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 400,
    letterSpacing: 'normal',
  },
}));

const useIsTextTruncated = (text: string) => {
  const ref = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setIsTruncated(el.scrollWidth > el.clientWidth);
  }, []);

  useLayoutEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    const root = el.parentElement;
    if (root) observer.observe(root);

    return () => observer.disconnect();
  }, [measure, text]);

  return { ref, isTruncated };
};

/** Import file group-row title: truncates `fileName`, keeps ` · date – time` visible. */
export const ImportFileRowLabel: React.FC<{ imp: ProjectElementImport }> = ({
  imp,
}) => {
  const { classes } = useStyles();
  const { fileName, dateTime, fullLabel } = getImportLabelParts(imp);
  const { ref, isTruncated } = useIsTextTruncated(fileName);

  return (
    <Box className={classes.root}>
      <Tooltip
        title={isTruncated ? fullLabel : ''}
        placement="top"
        disableInteractive
        enterDelay={400}
      >
        <Box ref={ref} className={classes.fileName} component="span">
          {fileName}
        </Box>
      </Tooltip>
      <Box className={classes.dateTime} component="span">
        {` · ${dateTime}`}
      </Box>
    </Box>
  );
};
